import type {
  AddOnKey,
  DmmProductSizing,
  DmmProductVariant,
  DmmSegmentKey,
  DmmSegmentReview,
  ProductKey,
} from './types';

export const DMM_PRODUCT_VARIANTS: Partial<Record<ProductKey, Record<string, DmmProductVariant>>> = {
  plant_super_boost: {
    '8 oz bottle': {
      sizeLabel: '8 oz bottle',
      variantId: '44416311689398',
      imageUrl:
        'https://cdn.shopify.com/s/files/1/0573/0097/5798/files/Plant_Super_Boost_8oz_-_Hero_Image_White.jpg?v=1779968839',
    },
    '32 oz bottle': {
      sizeLabel: '32 oz bottle',
      variantId: '44416311722166',
      imageUrl:
        'https://cdn.shopify.com/s/files/1/0573/0097/5798/files/Plant_Super_Boost_-_Hero_Image_White.jpg?v=1779968838',
    },
    '1 gallon jug': {
      sizeLabel: '1 gallon jug',
      variantId: '44416311754934',
      imageUrl:
        'https://cdn.shopify.com/s/files/1/0573/0097/5798/files/Plant_Super_Boost_128oz_-_Hero_Image_White.jpg?v=1779968839',
    },
  },
  crab_kelp_amino_acids: {
    '2 lb bag': {
      sizeLabel: '2 lb bag',
      variantId: '44244057292982',
      imageUrl:
        'https://cdn.shopify.com/s/files/1/0573/0097/5798/files/Organic_Fertilizer_-_Hero_Image_White.jpg?v=1779969065',
    },
    '10 lb bag': {
      sizeLabel: '10 lb bag',
      variantId: '44244057325750',
      imageUrl:
        'https://cdn.shopify.com/s/files/1/0573/0097/5798/files/Organic_Fertilizer_CKAA_10lb-_Hero_Image_White.jpg?v=1779969066',
    },
  },
  super_soil: {
    '10 lb bag': {
      sizeLabel: '10 lb bag',
      variantId: '42496584777910',
      imageUrl:
        'https://cdn.shopify.com/s/files/1/0573/0097/5798/files/Super_Soil_-_Hero_Image_White.jpg?v=1779968723',
    },
  },
};

// Why this matters: one offer sentence must stay accurate for single, multi-unit, and project-based plans.
export const DMM_SUBSCRIPTION_BRIDGE =
  'Subscribe and Save offers 30 percent off your first order and 15 percent off future subscription orders. Choose a delivery schedule that matches your plan.';

const CARD_COPY = {
  few: 'Your size: the 8 oz bottle and the 2 lb bag. Small, right, and nothing wasted.',
  dozen: 'Your size: the 32 oz bottle and the 10 lb bag. One order carries you a season.',
  full_yard: 'Your size: one 32 oz bottle and one 10 lb bag a month while things are growing.',
  big_property: 'Your size: the gallon. This is a working property and it feeds like one.',
  lawn_small: 'Your lawn runs on one 32 oz bottle a month. Mow, water, pour.',
  lawn_big: 'Your yard sits between sizes. The gallon means never running short mid-season.',
  lawn_large: 'An acre of living lawn runs on the gallon, once a month.',
} as const;

const sizing = (
  sizeLabel: string,
  quantity: string,
  cadence: string,
  duration: string,
  dose: string,
  cardCopy: string
): DmmProductSizing => {
  return {
    sizeLabel,
    quantity,
    cadence,
    duration,
    dose,
    cardCopy,
    subscriptionBridge: DMM_SUBSCRIPTION_BRIDGE,
  };
};

export const DMM_SIZING_BY_SIGNAL: Record<string, Partial<Record<ProductKey, DmmProductSizing>>> = {
  few: {
    plant_super_boost: sizing(
      '8 oz bottle',
      '1 bottle',
      'Monthly in the growing season',
      'About 3 to 4 months',
      'Pour about 2 oz per gallon of water monthly.',
      CARD_COPY.few
    ),
    crab_kelp_amino_acids: sizing(
      '2 lb bag',
      '1 bag',
      'Monthly in the growing season',
      'Lasts months',
      'Feed monthly in the growing season.',
      CARD_COPY.few
    ),
    super_soil: sizing(
      '10 lb bag',
      '1 to 2 bags for most small setups',
      'At planting, then refresh from the bottom once a year',
      'Permanent, with yearly refresh',
      '1 bag per 5-gallon pot. Most small setups need 1 to 2 bags.',
      CARD_COPY.few
    ),
  },
  dozen: {
    plant_super_boost: sizing(
      '32 oz bottle',
      '1 bottle',
      'Monthly in the growing season',
      'About 3 months',
      'Pour monthly. One bottle covers a collection for about 3 months.',
      CARD_COPY.dozen
    ),
    crab_kelp_amino_acids: sizing(
      '10 lb bag',
      '1 bag every 3 months',
      'Every 3 months in the growing season',
      'One season',
      'Use one 10 lb bag every 3 months, or use the 2 lb bag monthly.',
      CARD_COPY.dozen
    ),
    super_soil: sizing(
      '10 lb bag',
      '3 to 4 bags for typical collections',
      'At planting, then refresh from the bottom once a year',
      'Permanent, with yearly refresh',
      '1 bag per 5-gallon pot. Typical collections run 3 to 4 bags.',
      CARD_COPY.dozen
    ),
  },
  full_yard: {
    plant_super_boost: sizing(
      '32 oz bottle',
      '1 bottle per month',
      'Monthly in the growing season',
      'One month per bottle',
      'Use one 32 oz bottle per month while things are growing.',
      CARD_COPY.full_yard
    ),
    crab_kelp_amino_acids: sizing(
      '10 lb bag',
      '1 bag per month in season',
      'Monthly in the growing season',
      'One month per bag',
      'Use one 10 lb bag per month in season. Beds: 4 lb per 100 sq ft at planting, then 1 lb per 100 sq ft monthly.',
      CARD_COPY.full_yard
    ),
    super_soil: sizing(
      '10 lb bag',
      'By container count',
      'At planting, then refresh from the bottom once a year',
      'Permanent, with yearly refresh',
      'Use 1 bag per 5-gallon pot. Beds refresh from the bottom yearly.',
      CARD_COPY.full_yard
    ),
  },
  big_property: {
    plant_super_boost: sizing(
      '1 gallon jug',
      '1 jug per month',
      'Monthly in the growing season',
      'One month per jug',
      'Use one gallon jug per month while things are growing.',
      CARD_COPY.big_property
    ),
    crab_kelp_amino_acids: sizing(
      '10 lb bag',
      '10 lb bags on subscription',
      'Monthly in the growing season',
      'One month per bag',
      'Use 10 lb bags monthly or better. Trees: 1 oz per inch of trunk per month.',
      CARD_COPY.big_property
    ),
    super_soil: sizing(
      '10 lb bag',
      'By project',
      'At planting, then refresh from the bottom once a year',
      'Permanent, with yearly refresh',
      'Quote the 1 bag per 5-gallon pot rule and point to the bundle builder.',
      CARD_COPY.big_property
    ),
  },
};

export const DMM_LAWN_SIZING: Record<string, Partial<Record<ProductKey, DmmProductSizing>>> = {
  small: {
    plant_super_boost: sizing(
      '32 oz bottle',
      '1 bottle per month',
      'Monthly over the grass',
      'One month per bottle',
      'Use one 32 oz bottle per month over the grass with a hose-end sprayer.',
      CARD_COPY.lawn_small
    ),
    crab_kelp_amino_acids: sizing(
      '10 lb bag',
      'Optional, by lawn area',
      '3 to 4 times per year in the growing season',
      'One growing season',
      'Optional: 5 lb per 1,000 sq ft, 3 to 4 times a year.',
      CARD_COPY.lawn_small
    ),
  },
  tidy_lot: {
    plant_super_boost: sizing(
      '32 oz bottle',
      '1 bottle per month',
      'Monthly over the grass',
      'One month per bottle',
      'Use one 32 oz bottle per month over the grass with a hose-end sprayer.',
      CARD_COPY.lawn_small
    ),
    crab_kelp_amino_acids: sizing(
      '10 lb bag',
      'Optional, by lawn area',
      '3 to 4 times per year in the growing season',
      'One growing season',
      'Optional: 5 lb per 1,000 sq ft, 3 to 4 times a year.',
      CARD_COPY.lawn_small
    ),
  },
  suburban: {
    plant_super_boost: sizing(
      '32 oz bottle',
      '1 bottle per month',
      'Monthly over the grass',
      'One month per bottle',
      'Use one 32 oz bottle per month over the grass with a hose-end sprayer.',
      CARD_COPY.lawn_small
    ),
    crab_kelp_amino_acids: sizing(
      '10 lb bag',
      'Optional, by lawn area',
      '3 to 4 times per year in the growing season',
      'One growing season',
      'Optional: 5 lb per 1,000 sq ft, 3 to 4 times a year.',
      CARD_COPY.lawn_small
    ),
  },
  big: {
    plant_super_boost: sizing(
      '1 gallon jug',
      '1 jug per month',
      'Monthly over the grass',
      'One month per jug',
      'Use 32 oz to 1 gallon per month. Recommend the gallon if beds and trees are also picked.',
      CARD_COPY.lawn_big
    ),
    crab_kelp_amino_acids: sizing(
      '10 lb bag',
      'Optional, by lawn area',
      '3 to 4 times per year in the growing season',
      'One growing season',
      'Optional: 5 lb per 1,000 sq ft, 3 to 4 times a year.',
      CARD_COPY.lawn_big
    ),
  },
  large: {
    plant_super_boost: sizing(
      '1 gallon jug',
      '1 jug per month',
      'Monthly over the grass',
      'One month per jug',
      'Use one gallon jug per month over the grass.',
      CARD_COPY.lawn_large
    ),
    crab_kelp_amino_acids: sizing(
      '10 lb bag',
      'Optional, by lawn area',
      '3 to 4 times per year in the growing season',
      'One growing season',
      'Optional: 10 lb bag, 3 to 4 times a year.',
      CARD_COPY.lawn_large
    ),
  },
};

export const DMM_PRODUCT_BLURBS: Record<ProductKey, string> = {
  plant_super_boost:
    'Your dirt went dead, and dead dirt cannot grow a living thing. Plant Super Boost brings it back to life. In every drop there are 1,227 kinds of good fungi and bacteria, billions of tiny helpers that feed your roots what they could never reach alone. You cannot burn it. You cannot overdose it. It is life, not salt.',
  crab_kelp_amino_acids:
    'Real food for your plants, made from the sea and the land. It is 7-4-4, and it feeds slow over a whole month, so it never burns and you cannot overdose it. It feeds your plant directly and it feeds the life in your soil, a little fast and a lot slow. It smells like the ocean, never like rotting fish.',
  super_soil:
    'A home for your roots that does not rot, pack down, or drown your plants. Most bagged mix is wood and peat that breaks down into a wet brick, and the roots suffocate. Super Soil is mineral, so it stays open and full of air for years. Plant into it once. It is close to the last bag of soil you will ever buy.',
  the_bundle:
    'The life, the food, and the home, together. Plant Super Boost wakes your soil, Crab, Kelp & Amino Acids feeds it, and Super Soil gives the roots a home that lasts. The three were built to work as one, and buying them together is the smart way to start.',
};

export const DMM_ADDON_BLURBS: Record<AddOnKey, string> = {
  chameleon_sprayer:
    'The easy way to feed your whole yard at once. Most sprayers strain the living microbes right out. This one does not. You hook it to your hose, and the life and food go out over the lawn and beds in minutes. We hand pick it because it is the one that treats living soil right.',
  biochar:
    'Little pieces of charcoal full of tiny rooms where the good life in your soil moves in and multiplies. It holds water and air right at the roots. Mix it into any pot or bed once, and it works for years. We carry the clean, organic kind.',
  rice_hulls:
    "Nature's mulch and drainage in one. Spread on top, they lock in water and keep weeds down. Mixed in, they keep your soil loose and full of air. Light, clean, and natural. We carry the parboiled kind that lasts.",
  coco_coir:
    'Dried blocks of coco coir that puff up into soft, fluffy growing material when you add water. They hold moisture like a sponge and keep soil light. One small brick makes a big batch. Clean, natural, and easy to store.',
  castile_soap:
    'A gentle, plant based soap that doubles as a safe bug spray. A few drops in water knocks out aphids, gnats, and mites without harsh chemicals. Safe around kids and pets. Natural and simple, the way pest control should be.',
  diatomaceous_earth:
    'A fine, natural powder made from ancient sea fossils. It stops crawling bugs the old, clean way, no chemicals at all. Dust it around plants that pests keep chewing. Safe to use where you grow your food.',
  sulfur_dust:
    'A natural mineral dust that fights fungus and keeps certain bugs off your plants. It is one of the oldest, gentlest tools a gardener has. A light dusting protects leaves and fruit. Simple, natural, and proven.',
};

export const DMM_SEGMENT_PRIORITY: DmmSegmentKey[] = [
  'fruit_trees',
  'vegetables',
  'flowers',
  'houseplants',
  'lawn',
  'everything',
];

export const DMM_SEGMENT_BY_GROW: Partial<Record<string, DmmSegmentKey>> = {
  vegetables: 'vegetables',
  herbs: 'vegetables',
  flowers: 'flowers',
  pollinator: 'flowers',
  houseplants: 'houseplants',
  fruit_trees: 'fruit_trees',
  berries_grapes: 'fruit_trees',
  lawn: 'lawn',
  trees_shrubs: 'everything',
  succulents: 'everything',
};

export const DMM_SEGMENT_REVIEWS: Partial<Record<DmmSegmentKey, DmmSegmentReview>> = {
  fruit_trees: {
    setup: 'For fruit-tree growers',
    quote: 'Not only did the trees explode in size, they also started to bear fruit.',
    reviewer: 'Flo L.',
    product: 'Plant Super Boost',
    verification: 'Verified Review',
  },
  flowers: {
    setup: 'For flower growers',
    quote:
      'My almost-dead magnolia just came back to life. We were skeptical at first… but then we started seeing results in about 2 weeks.',
    reviewer: 'Olga M.',
    product: 'Plant Super Boost',
    verification: 'Verified Review',
  },
  houseplants: {
    setup: 'For houseplant growers',
    quote:
      'High-quality product. I used it on both my garden plants and my house plants. I also had a poinsettia that was basically dead. I added this with biochar, and within two days, I had tons of new growth. Mind blown.',
    reviewer: 'Jessica S.',
    product: 'Plant Super Boost',
    verification: 'Verified Buyer',
  },
  everything: {
    setup: 'For gardeners growing across the whole picture',
    quote:
      'This is hands down the BEST stuff for your plants. Had several on the brink of the abyss, only to be saved by this magic. Used them on my healthy plants, and they ALL got even better.',
    reviewer: 'Christi R.',
    product: 'Plant Super Boost',
    verification: 'Verified Buyer',
  },
};

export function getDmmVariant(productKey: ProductKey, sizeLabel?: string): DmmProductVariant | undefined {
  if (!sizeLabel) return undefined;
  return DMM_PRODUCT_VARIANTS[productKey]?.[sizeLabel];
}
